import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { MDSBadgeVariant } from "@/mds/components/badge";
import { MdsIconKind } from "@/design-system/components/icon";
import {
  SystemAudioPermissionModalContent,
  SystemAudioPermissionModalRestartRow,
  SystemAudioPermissionModalRow,
} from "@/components/desktop/system-audio/permissions/system-audio-permission-modal-content";
import { RecallPermissionKind, RecallPermissionStatus } from "@/modules/electron/types";
import { logger } from "@/modules/logger";
import { objectModule } from "@/modules/object";
import { trackEvent, TrackedEvent } from "@/domains/metrics";
import { sleep } from "@/domains/event-loop/synchronization";

type PermissionStatusMap = Partial<Record<RecallPermissionKind, RecallPermissionStatus>>;

type Props = {
  isOpen: boolean;
  onClose: () => void;
  permissionStatus: PermissionStatusMap;
  onRequestPermission: (permission: RecallPermissionKind) => Promise<void>;
  onOpenPermissionPane: (permission: RecallPermissionKind) => Promise<void>;
  onRestart: () => Promise<void>;
  moreInfoHref?: string;
  onMoreInfoClick?: () => void;
};

export const SystemAudioPermissionModal: FC<Props> = ({
  isOpen,
  onClose,
  permissionStatus,
  onRequestPermission,
  onOpenPermissionPane,
  onRestart,
  moreInfoHref,
  onMoreInfoClick,
}: Props) => {
  const [hasOpenedSystemPreferences, setHasOpenedSystemPreferences] = useState(false);
  const [loadingPermission, setLoadingPermission] = useState<RecallPermissionKind | null>(null);
  const hasTrackedViewRef = useRef(false);
  const deniedPermissions = useMemo(() => {
    return (
      Object.entries(permissionStatus ?? {}) as [RecallPermissionKind, RecallPermissionStatus][]
    )
      .filter(([, status]) => status === RecallPermissionStatus.Denied)
      .map(([permission]) => permission);
  }, [permissionStatus]);

  useEffect(() => {
    if (!isOpen) {
      setHasOpenedSystemPreferences(false);
      hasTrackedViewRef.current = false;

      return;
    }

    if (hasTrackedViewRef.current) return;

    trackEvent(TrackedEvent.DesktopSystemAudioPermissionsModalView, {
      flow: "recall",
      denied_permissions: deniedPermissions,
    });

    hasTrackedViewRef.current = true;
  }, [isOpen, deniedPermissions]);

  const showRestartPrompt = useMemo(() => {
    if (!hasOpenedSystemPreferences) return false;

    return deniedPermissions.length > 0;
  }, [deniedPermissions, hasOpenedSystemPreferences]);

  const handlePermissionClick = useCallback(
    async (kind: RecallPermissionKind, status: RecallPermissionStatus) => {
      if (loadingPermission) return;

      logger.info({
        message: "[SystemAudioPermissionModal] Permission button clicked",
        info: { kind, status },
      });

      trackEvent(TrackedEvent.DesktopSystemAudioPermissionInteract, {
        flow: "recall",
        permission: kind,
        status_before: status,
      });

      setLoadingPermission(kind);

      await sleep(1500);

      try {
        if (status === RecallPermissionStatus.Denied) {
          setHasOpenedSystemPreferences(true);

          trackEvent(TrackedEvent.DesktopSystemAudioPermissionOpenPreferences, {
            flow: "recall",
            permission: kind,
          });

          logger.info({
            message: "[SystemAudioPermissionModal] Opening system preferences",
            info: { kind },
          });

          await onOpenPermissionPane(kind);
        } else {
          logger.info({
            message: "[SystemAudioPermissionModal] Requesting permission",
            info: { kind },
          });

          await onRequestPermission(kind);
        }
      } finally {
        setLoadingPermission(null);
      }
    },
    [loadingPermission, onOpenPermissionPane, onRequestPermission]
  );

  const handleRestart = useCallback(async () => {
    trackEvent(TrackedEvent.DesktopSystemAudioPermissionsRestartClick, {
      denied_permissions: deniedPermissions,
      has_opened_system_preferences: hasOpenedSystemPreferences,
    });

    logger.info({ message: "[SystemAudioPermissionModal] Restart button clicked" });

    try {
      await onRestart();
    } catch (error) {
      logger.error({
        message: "[SystemAudioPermissionModal] Failed to restart after permission change",
        info: { error: objectModule.safeErrorAsJson(error as Error) },
      });
    }
  }, [deniedPermissions, hasOpenedSystemPreferences, onRestart]);

  const handleClose = useCallback(() => {
    setHasOpenedSystemPreferences(false);
    setLoadingPermission(null);
    onClose();
  }, [onClose]);

  const permissionRows = useMemo<SystemAudioPermissionModalRow[]>(() => {
    const items: Array<{
      id: string;
      label: string;
      kind: RecallPermissionKind;
      supportingText?: string;
    }> = [
      {
        id: "microphone",
        label: "Microphone",
        kind: RecallPermissionKind.Microphone,
      },
      {
        id: "accessibility",
        label: "Accessibility",
        kind: RecallPermissionKind.Accessibility,
      },
      {
        id: "screen-capture",
        label: "Screen recording and audio",
        kind: RecallPermissionKind.ScreenCapture,
      },
    ];

    return items.map<SystemAudioPermissionModalRow>((item, index) => {
      const status = permissionStatus[item.kind] ?? RecallPermissionStatus.NotRequested;

      const showAction = status !== RecallPermissionStatus.Granted;

      const statusLabel = (() => {
        switch (status) {
          case RecallPermissionStatus.Granted:
            return {
              label: "Enabled",
              variant: MDSBadgeVariant.Emphasis,
              icon: MdsIconKind.CheckCircleSolid,
            } as const;
          case RecallPermissionStatus.Denied:
            return {
              label: "Denied",
              variant: MDSBadgeVariant.Negative,
              icon: MdsIconKind.Warning,
            } as const;
          default:
            return {
              label: "Needs permission",
              variant: MDSBadgeVariant.Default,
              icon: MdsIconKind.InfoCircle,
            } as const;
        }
      })();

      return {
        id: item.id,
        index: index + 1,
        label: item.label,
        supportingText: item.supportingText,
        status: statusLabel,
        action: showAction
          ? {
              label:
                status === RecallPermissionStatus.Denied
                  ? "Open System Preferences to enable"
                  : "Enable",
              onClick: () => handlePermissionClick(item.kind, status),
              isLoading: loadingPermission === item.kind,
            }
          : undefined,
      };
    });
  }, [handlePermissionClick, loadingPermission, permissionStatus]);

  const restartRow: SystemAudioPermissionModalRestartRow | undefined = useMemo(() => {
    if (!showRestartPrompt) return undefined;

    return {
      index: permissionRows.length + 1,
      label: "Restart Mem",
      actionLabel: "Restart",
      onClick: handleRestart,
    };
  }, [handleRestart, permissionRows, showRestartPrompt]);

  return (
    <SystemAudioPermissionModalContent
      isOpen={isOpen}
      onClose={handleClose}
      rows={permissionRows}
      restartRow={restartRow}
      moreInfoHref={moreInfoHref}
      onMoreInfoClick={onMoreInfoClick}
    />
  );
};
